import {
  useDrawingContext,
  useOpenLayersContext,
} from '@ais-ui/react-openlayers';
import { ReactNode } from 'react';

import { AISMapProvider } from './AISMapContext';
import { AISMapLayer } from './AISMapLayer';
import { ListenForShipClicks } from './ListenForShipClicks';
import { MapPanel } from './MapPanel/MapPanel';
import { useDrawings } from './useDrawings';
import { useSettings } from '../Settings/SettingsProvider';
import { useZoomAndSelectShip } from './useZoomAndSelectShip';
import { PortLayer } from './PortLayer';
import { ListenForLayerClicks } from './ListenForLayerClicks';
import {
  useSelectedGpsLostGridCellStyling,
  useSelectedPortStyling,
  useSelectedSourceIdentifiersStyling,
} from './useSelectedStyling';
import { CoverageLayer } from './Coverage/CoverageLayer';
import { useCursorCoordinates } from './useCursorCoordinates';
import { useCheckSearchAndRescue } from '../../../../../libs/websockets/src/lib/ol-server/useCheckSearchAndRescue';
import { TargetsOnMapProvider } from './TargetsOnMapContext';
import { HoverMap } from './HoverMap';
import { AtonProvider } from '../Atons/AtonContext';
import { RoutesProvider } from '../Routes/RoutesContext';
import { useZoomToExternalShip } from './useZoomToExternalShip';
import { usePlayReplay } from '../Replay/usePlayReplay';
import { GPSLostLayer } from './GpsLost/GPSLostLayer';
import { useOnStartLayers } from './LayersOnStart/useOnStartLayers';

export interface MapComponentsProps {
  children?: ReactNode;
}

export interface AISComponentsProps {
  children?: ReactNode;
}

function AISComponents({ children }: AISComponentsProps) {
  const drawingContext = useDrawingContext();
  const { map } = useOpenLayersContext();
  useZoomAndSelectShip();
  useCursorCoordinates();
  useZoomToExternalShip();
  usePlayReplay();

  const { settings } = useSettings();
  useCheckSearchAndRescue(
    Boolean(settings['ribbonPlugin.settings']?.emergency_alerts_mdm)
  );
  useSelectedPortStyling();
  useSelectedSourceIdentifiersStyling();
  useSelectedGpsLostGridCellStyling();

  return (
    <>
      <AISMapLayer />
      <PortLayer />
      <CoverageLayer />
      <GPSLostLayer />
      {!drawingContext.geometryType && (
        <>
          <ListenForShipClicks></ListenForShipClicks>
          <ListenForLayerClicks map={map}></ListenForLayerClicks>
          <HoverMap />
        </>
      )}
      <MapPanel></MapPanel>
      {children}
    </>
  );
}

export function MapComponents({ children }: MapComponentsProps) {
  // This hook takes care of everything that's drawn in MDM
  useDrawings();
  useOnStartLayers();

  return (
    <AISMapProvider>
      <AtonProvider>
        <TargetsOnMapProvider>
          <RoutesProvider>
            <AISComponents>{children}</AISComponents>
          </RoutesProvider>
        </TargetsOnMapProvider>
      </AtonProvider>
    </AISMapProvider>
  );
}
